import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import SAvatarStemdoer from '@/components/stemdo-components/s-avatar-stemdoer/SAvatarStemdoer.vue';
import SInputNumber from '@/components/stemdo-components/s-input-number/SInputNumber.vue';
import TagInterest from './tag-interest/TagInterest.vue';
import ProjectSelector from '@/components/stemdo-components/project-selector/ProjectSelector.vue';
import { Stemdoer } from '@/shared/model/stemdoer.model';
import { useStore } from '@/plugins/vuex';

export type CardOptions = {
  isRateVisible?: boolean;
  isRateEditable?: boolean;
  isDeletable?: boolean;
  isDisabled?: boolean;
  isClickable?: boolean;
  displayInterestTag?: boolean;
  onDelete?: (stemdoerId: string) => void;
  onRateChange?: (stemdoerId: string, newRate: number) => void;
  onClick?: (stemdoerId: string) => void;
  onProjectChange?: (stemdoerId: string, project: any) => void;
  projects?: any;
};

export default defineComponent({
  props: {
    stemdoer: {
      type: Object as () => Stemdoer,
      required: true
    },
    rate: {
      type: Number
    },
    cardOptions: {
      type: Object as () => CardOptions,
      required: false
    }
  },
  components: {
    SAvatarStemdoer,
    SInputNumber,
    TagInterest,
    ProjectSelector
  },
  setup(props: { stemdoer: Stemdoer; rate: number; cardOptions?: CardOptions }) {
    const firstFourTechnologyScores = computed(() => props.stemdoer.technologyScores.slice(0, 4));
    const store = useStore();
    const projectItems = computed(() => (Array.isArray(props.cardOptions?.projects) ? [...props.cardOptions.projects] : []));
    const checkoutStemdoerRates = computed(() => store.state.checkoutStore.stemdoerRates);
    const initialProject = computed(() => {
      const found = checkoutStemdoerRates.value.find(r => r.stemdoerId === props.stemdoer.id);
      if (found && found.projectId && projectItems.value.length) {
        const projectId =
          typeof found.projectId === 'object' && found.projectId !== null && 'id' in found.projectId ? found.projectId.id : found.projectId;
        return projectItems.value.find((p: any) => p.id?.toString() === projectId?.toString()) || null;
      }
      return null;
    });
    const selectedProject = ref<string | null>(null);

    const selectedProjectObj = computed(() => {
      return projectItems.value.find((p: any) => p.id?.toString() === selectedProject.value?.toString()) || null;
    });

    onMounted(() => {
      const exists = store.state.checkoutStore.stemdoerRates.some(r => r.stemdoerId === props.stemdoer.id);
      if (!exists && typeof props.rate === 'number') {
        store.dispatch('checkoutStore/updateStemdoerRate', {
          stemdoerId: props.stemdoer.id,
          rate: props.rate
        });
      }
      const initial = initialProject.value;
      if (initial && typeof initial === 'object' && 'id' in initial) {
        selectedProject.value = initial.id;
      } else {
        selectedProject.value = null;
      }
      let rates = Array.isArray(store.state.checkoutStore.stemdoerRates) ? [...store.state.checkoutStore.stemdoerRates] : [];
      rates = rates.map(r => {
        if (r.stemdoer && r.stemdoer.id) return r;
        if (r.stemdoerId) {
          if (props.stemdoer.id === r.stemdoerId) {
            return {
              rate: r.rate,
              stemdoer: props.stemdoer,
              projectId: r.projectId ?? null
            };
          }
        }
        return r;
      });
      if (props.cardOptions.isRateEditable && !props.cardOptions.onRateChange) {
        throw new Error('onRateChange is required when rate is editable');
      }
      if (props.cardOptions.isDeletable && !props.cardOptions.onDelete) {
        throw new Error('onDelete is required when card is deletable');
      }
      if (props.cardOptions.isClickable && !props.cardOptions.onClick) {
        throw new Error('onClick is required when card is clickable');
      }
    });

    watch(
      [projectItems, initialProject],
      ([items, initial]) => {
        if (initial && typeof initial === 'object' && 'id' in initial) {
          setDefaultProject(initial.id);
        } else {
          setDefaultProject(null);
        }
      },
      { immediate: true }
    );

    const updateStemdoerRateInStore = (newRate: number) => {
      const rates = Array.isArray(store.state.checkoutStore.stemdoerRates) ? [...store.state.checkoutStore.stemdoerRates] : [];
      const idx = rates.findIndex(r => r.stemdoer && r.stemdoer.id === props.stemdoer.id);
      const rateObj = {
        rate: newRate,
        stemdoer: props.stemdoer,
        projectId: selectedProject.value ?? null
      };
      if (idx !== -1) {
        rates[idx] = rateObj;
      } else {
        rates.push(rateObj);
      }
    };

    const updateStemdoerProjectInStore = (projectId: string | null) => {
      const rates = Array.isArray(store.state.checkoutStore.stemdoerRates) ? [...store.state.checkoutStore.stemdoerRates] : [];
      const idx = rates.findIndex(r => r.stemdoer && r.stemdoer.id === props.stemdoer.id);
      if (idx !== -1) {
        rates[idx] = {
          ...rates[idx],
          projectId: projectId ?? null,
          stemdoer: props.stemdoer
        };
      }
    };

    function handleProjectChange(projectId: string | null) {
      selectedProject.value = projectId;
      updateStemdoerProjectInStore(projectId);
      if (props.cardOptions.onProjectChange) {
        props.cardOptions.onProjectChange(props.stemdoer.id, projectId);
      }
    }

    const onRateChange = (newRate: number) => {
      updateStemdoerRateInStore(newRate);
      if (props.cardOptions.onRateChange) {
        props.cardOptions.onRateChange(props.stemdoer.id, newRate);
      }
    };

    const onDeleteClick = (stemdoerId: string) => {
      props.cardOptions.onDelete(stemdoerId);
    };

    const onClick = (stemdoerId: string) => {
      props.cardOptions.onClick(stemdoerId);
    };

    const removeProject = () => {
      selectedProject.value = null;
      if (props.cardOptions.onProjectChange) {
        props.cardOptions.onProjectChange(props.stemdoer.id, null);
        handleProjectChange(null);
      }
    };

    function setDefaultProject(projectId: string | null) {
      if (!projectId) {
        selectedProject.value = null;
        return;
      }
      const found = projectItems.value.find((p: any) => p.id?.toString() === projectId.toString());
      if (found) {
        handleProjectChange(found.id);
      } else {
        selectedProject.value = null;
      }
    }

    return {
      firstFourTechnologyScores,
      selectedProject,
      selectedProjectObj,
      projectItems,
      setDefaultProject,
      onRateChange,
      onDeleteClick,
      onClick,
      handleProjectChange,
      removeProject
    };
  }
});
